import React from 'react';
import { Lock, Shield, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const LockControls = ({ 
  isLocked, 
  lockPassword, 
  setLockPassword, 
  lockDuration, 
  setLockDuration,
  timeUnit,
  setTimeUnit,
  handleLock,
  timeRemaining,
  formatTime,
  t 
}) => {
  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white border-2 border-gray-200">
        <div className="flex items-center space-x-3 rtl:space-x-reverse mb-6">
          <div className="bg-black text-white p-3 rounded-full">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-semibold">{t('passwordLockMode')}</h3>
            <p className="text-sm text-gray-600">{t('newFeature')}</p>
          </div>
        </div>

        {!isLocked ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('setPassword')}</label>
              <Input
                type="password"
                placeholder={t('passwordPlaceholder')}
                value={lockPassword}
                onChange={(e) => setLockPassword(e.target.value)}
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">{t('lockDuration')}</label>
              <div className="flex space-x-2 rtl:space-x-reverse">
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={lockDuration}
                  onChange={(e) => setLockDuration(parseInt(e.target.value) || 1)}
                  className="w-24"
                />
                <Select value={timeUnit} onValueChange={setTimeUnit}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('selectUnit')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutes">{t('minutes')}</SelectItem>
                    <SelectItem value="hours">{t('hours')}</SelectItem>
                    <SelectItem value="days">{t('days')}</SelectItem>
                    <SelectItem value="weeks">{t('weeks')}</SelectItem>
                    <SelectItem value="months">{t('months')}</SelectItem>
                    <SelectItem value="years">{t('years')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t('durationNote')}
              </p>
            </div>

            <Button 
              onClick={handleLock}
              className="w-full bg-black text-white hover:bg-gray-800 flex items-center justify-center space-x-2 rtl:space-x-reverse"
              disabled={!lockPassword}
            >
              <Lock className="w-4 h-4" />
              <span>{t('lockPhoneFor')} {lockDuration} {lockDuration === 1 ? t(timeUnit.slice(0, -1)) : t(timeUnit)}</span>
            </Button>

            <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700">
              <p className="font-medium mb-2">{t('howItWorks')}</p>
              <ul className="space-y-1 text-sm">
                <li>• {t('step1')}</li>
                <li>• {t('step2')}</li>
                <li>• {t('step3')}</li>
                <li>• {t('step4')}</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                <Lock className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-900">{t('activeLock')}</span>
              </div>
              <p className="text-sm text-green-800">{t('activeLockDesc')}</p>
            </div>

            <div className="text-center py-6">
              <div className="text-5xl font-light mb-2 break-words">{formatTime(timeRemaining)}</div>
              <p className="text-gray-600">{t('timeRemaining')}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 text-center">
                {t('lockReleaseNote')}
              </p>
            </div>
          </div>
        )}
      </Card>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">{t('proTip')}</p>
            <p>{t('proTipText')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LockControls;
